import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useDataProvider } from 'react-admin';

import Box from '@material-ui/core/Box';
import Chip from '@material-ui/core/Chip';
import CircularProgress from '@material-ui/core/CircularProgress';
import Divider from '@material-ui/core/Divider';
import Typography from '@material-ui/core/Typography';
import makeStyles from '@material-ui/core/styles/makeStyles';
import CheckCircleOutline from '@material-ui/icons/CheckCircleOutline';
import InfoOutlined from '@material-ui/icons/InfoOutlined';

import { Dialog } from 'components/common';
import { formatCentsPrice } from 'services/helpers/formatPrice';

// RLPR-20420 on-demand Stripe Financial Connections balance check of an ACH bank account
export interface AchBalanceCheckResponse {
  paymentMethodId: string;
  financialConnectionsAccount: string;
  bankName?: string;
  lastDigits?: string;
  accountStatus: string;
  refreshRequested: boolean;
  refreshStatus?: string | null;
  lastAttemptedAt?: string | null;
  nextRefreshAvailableAt?: string | null;
  availableBalanceCents?: number | null;
  currentBalanceCents?: number | null;
  balanceAsOf?: string | null;
  unsettledAchAmountCents?: number | null;
  sufficientForUnsettled?: boolean | null;
}

type Props = {
  open: boolean;
  onClose: () => void;
  // rollo user id (uuid), not the billing id
  userId: string;
} & (
  | { paymentMethodId: string; paymentIntent?: never }
  | { paymentMethodId?: never; paymentIntent: string }
);

const GREEN = '#2e7d32';
const GREEN_BG = '#edf7ed';
const AMBER = '#b26a00';
const AMBER_BG = '#fff4e5';

const useStyles = makeStyles((theme) => ({
  rows: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },

  row: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '16px',
    minHeight: 24,
  },

  label: {
    color: theme.palette.text.secondary,
    fontSize: 13,
    whiteSpace: 'nowrap',
  },

  value: {
    fontSize: 13,
    textAlign: 'right',
  },

  valueStrong: {
    fontSize: 13,
    fontWeight: 600,
    textAlign: 'right',
  },

  valueWithChip: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },

  divider: {
    margin: theme.spacing(1.5, 0),
  },

  chipActive: {
    backgroundColor: GREEN,
    color: '#fff',
    height: 20,
  },

  chipInactive: {
    backgroundColor: theme.palette.error.main,
    color: theme.palette.error.contrastText,
    height: 20,
  },

  chipFresh: {
    backgroundColor: theme.palette.primary.light,
    color: theme.palette.primary.contrastText,
    height: 20,
  },

  chipCached: {
    backgroundColor: AMBER_BG,
    color: AMBER,
    height: 20,
  },

  chipUnknown: {
    backgroundColor: theme.palette.grey[100],
    color: theme.palette.text.secondary,
    height: 20,
  },

  verdict: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '10px',
    marginTop: theme.spacing(2),
    padding: theme.spacing(1.5),
    borderRadius: 4,
    border: '1px solid transparent',
  },

  verdictOk: {
    backgroundColor: GREEN_BG,
    borderColor: GREEN,
    color: GREEN,
  },

  verdictShort: {
    backgroundColor: '#fdecea',
    borderColor: theme.palette.error.main,
    color: theme.palette.error.dark,
  },

  verdictUnknown: {
    backgroundColor: theme.palette.grey[100],
    borderColor: theme.palette.grey[400],
    color: theme.palette.text.secondary,
  },

  verdictTitle: {
    fontSize: 13,
    fontWeight: 600,
  },

  verdictText: {
    fontSize: 12,
  },

  checkedAt: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
    fontSize: 12,
    color: theme.palette.text.secondary,
  },

  previousResult: {
    marginBottom: theme.spacing(1.5),
    padding: theme.spacing(1),
    backgroundColor: AMBER_BG,
    color: AMBER,
    fontSize: 13,
    fontWeight: 600,
  },

  noteThrottle: {
    display: 'block',
    marginBottom: theme.spacing(1),
    color: theme.palette.text.primary,
  },
}));

const formatDateTime = (value?: string | Date | null) =>
  value
    ? new Date(value).toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      })
    : '-';

const formatCents = (value?: number | null) =>
  value === null || value === undefined ? 'n/a' : formatCentsPrice(value);

const isFuture = (value: string | null | undefined, now: number) =>
  !!value && new Date(value).getTime() > now;

const humanizeUntil = (value: string, now: number) => {
  const seconds = Math.max(0, Math.round((new Date(value).getTime() - now) / 1000));
  if (seconds < 90) {
    return `about ${Math.max(seconds, 1)} seconds`;
  }
  const minutes = Math.round(seconds / 60);
  if (minutes < 90) {
    return `about ${minutes} minutes`;
  }
  return `about ${Math.round(minutes / 60)} hours`;
};

const errorMessage = (error: any) =>
  [error?.body?.details, error?.body?.message, error, error?.message].find(
    (message) => typeof message === 'string' && message.trim().length > 0
  ) || 'Balance check failed';

const AchBalanceCheckDialog = ({
  open,
  onClose,
  userId,
  paymentMethodId,
  paymentIntent,
}: Props) => {
  const classes = useStyles();
  const dataProvider = useDataProvider();

  const [loading, setLoading] = useState<boolean>(false);
  const [result, setResult] = useState<AchBalanceCheckResponse | null>(null);
  const [checkedAt, setCheckedAt] = useState<Date | null>(null);
  const [error, setError] = useState<string | undefined>(undefined);
  const [now, setNow] = useState(Date.now);
  const dialogOpenRef = useRef(false);
  const requestSequenceRef = useRef(0);
  const activeRequestRef = useRef<number | null>(null);

  const runCheck = useCallback(async () => {
    if (!dialogOpenRef.current || activeRequestRef.current !== null) {
      return;
    }

    const hasPaymentMethod = typeof paymentMethodId === 'string' && !!paymentMethodId.trim();
    const hasPaymentIntent = typeof paymentIntent === 'string' && !!paymentIntent.trim();

    if (
      typeof userId !== 'string' ||
      !userId.trim() ||
      !(
        (hasPaymentMethod && paymentIntent === undefined) ||
        (hasPaymentIntent && paymentMethodId === undefined)
      )
    ) {
      setError('Unable to check balance: missing or invalid account information');
      return;
    }

    const requestId = ++requestSequenceRef.current;
    activeRequestRef.current = requestId;
    const isCurrentRequest = () =>
      dialogOpenRef.current && requestSequenceRef.current === requestId;

    try {
      setLoading(true);
      setError(undefined);

      const { data } = await dataProvider.post('checkAchBalance', {
        userId,
        data: { paymentMethodId, paymentIntent },
      });

      if (isCurrentRequest()) {
        setResult(data);
        setCheckedAt(new Date());
      }
    } catch (e) {
      if (isCurrentRequest()) {
        setError(errorMessage(e));
      }
    } finally {
      if (isCurrentRequest()) {
        activeRequestRef.current = null;
        setLoading(false);
      }
    }
  }, [dataProvider, userId, paymentMethodId, paymentIntent]);

  useLayoutEffect(() => {
    dialogOpenRef.current = open;
    setLoading(false);
    setError(undefined);
    setResult(null);
    setCheckedAt(null);

    if (open) {
      runCheck();
    }

    return () => {
      dialogOpenRef.current = false;
      requestSequenceRef.current += 1;
      activeRequestRef.current = null;
    };
  }, [open, runCheck]);

  const handleClose = () => {
    dialogOpenRef.current = false;
    requestSequenceRef.current += 1;
    activeRequestRef.current = null;
    onClose();
  };

  const nextRefreshAvailableAt = result?.nextRefreshAvailableAt;

  useEffect(() => {
    const currentTime = Date.now();
    setNow(currentTime);

    if (!open || !isFuture(nextRefreshAvailableAt, currentTime)) {
      return;
    }

    const updateClock = () => {
      if (!dialogOpenRef.current) {
        return;
      }

      const currentTime = Date.now();
      setNow(currentTime);

      if (!isFuture(nextRefreshAvailableAt, currentTime)) {
        window.clearInterval(interval);
      }
    };
    const interval = window.setInterval(updateClock, 1000);
    window.addEventListener('focus', updateClock);
    document.addEventListener('visibilitychange', updateClock);

    return () => {
      window.clearInterval(interval);
      window.removeEventListener('focus', updateClock);
      document.removeEventListener('visibilitychange', updateClock);
    };
  }, [open, nextRefreshAvailableAt]);

  const isActive = result?.accountStatus === 'active';
  const isPending = result?.refreshStatus === 'pending';
  const hasFutureRefresh = isFuture(nextRefreshAvailableAt, now);
  const throttled = !!result && !isPending && hasFutureRefresh;

  // the backend judges on the available balance and falls back to the current one
  const judgedCents =
    result?.availableBalanceCents !== null && result?.availableBalanceCents !== undefined
      ? result.availableBalanceCents
      : result?.currentBalanceCents;
  const judgedLabel =
    result?.availableBalanceCents !== null && result?.availableBalanceCents !== undefined
      ? 'available'
      : 'current';

  const renderRefreshValue = () => {
    if (!result) {
      return null;
    }

    if (result.refreshRequested || isPending) {
      const fresh = result.refreshStatus === 'succeeded';
      const failed = result.refreshStatus === 'failed';
      return (
        <span className={classes.valueWithChip}>
          <Typography className={classes.value}>{result.refreshStatus || 'requested'}</Typography>
          <Chip
            size="small"
            label={
              isPending
                ? 'Refresh pending'
                : fresh
                  ? 'Refreshed just now'
                  : failed
                    ? 'Refresh failed'
                    : 'Refresh status unknown'
            }
            className={
              fresh
                ? classes.chipFresh
                : isPending || failed
                  ? classes.chipCached
                  : classes.chipUnknown
            }
          />
        </span>
      );
    }

    return (
      <span className={classes.valueWithChip}>
        <Typography className={classes.value}>not run</Typography>
        <Chip size="small" label="Showing cached balance" className={classes.chipCached} />
      </span>
    );
  };

  const renderVerdict = () => {
    if (!result) {
      return null;
    }

    const unsettled = result.unsettledAchAmountCents;

    if (result.sufficientForUnsettled === true) {
      return (
        <Box className={`${classes.verdict} ${classes.verdictOk}`}>
          <CheckCircleOutline fontSize="small" />
          <div>
            <Typography className={classes.verdictTitle}>Balance covers unsettled ACH</Typography>
            <Typography className={classes.verdictText}>
              {formatCents(judgedCents)} {judgedLabel} against {formatCents(unsettled)} unsettled
            </Typography>
          </div>
        </Box>
      );
    }

    if (result.sufficientForUnsettled === false) {
      const shortBy =
        typeof unsettled === 'number' &&
        Number.isFinite(unsettled) &&
        typeof judgedCents === 'number' &&
        Number.isFinite(judgedCents)
          ? unsettled - judgedCents
          : null;
      return (
        <Box className={`${classes.verdict} ${classes.verdictShort}`}>
          <InfoOutlined fontSize="small" />
          <div>
            <Typography className={classes.verdictTitle}>
              Balance does not cover unsettled ACH
            </Typography>
            <Typography className={classes.verdictText}>
              {formatCents(judgedCents)} {judgedLabel} against {formatCents(unsettled)} unsettled
              {shortBy !== null && shortBy > 0 && `, short by ${formatCents(shortBy)}`}
            </Typography>
          </div>
        </Box>
      );
    }

    return (
      <Box className={`${classes.verdict} ${classes.verdictUnknown}`}>
        <InfoOutlined fontSize="small" />
        <div>
          <Typography className={classes.verdictTitle}>No balance data from the bank</Typography>
          <Typography className={classes.verdictText}>
            {!isActive
              ? 'The bank connection is not active, so Stripe cannot refresh it. The user has to reconnect the bank account.'
              : isPending
                ? 'Stripe is still refreshing the balance. Check again in a few seconds.'
                : 'The institution did not report a balance for this account.'}
          </Typography>
        </div>
      </Box>
    );
  };

  const renderRow = (label: string, value: React.ReactNode, strong = false) => (
    <Box key={label} className={classes.row}>
      <Typography className={classes.label}>{label}</Typography>
      {typeof value === 'string' ? (
        <Typography className={strong ? classes.valueStrong : classes.value}>{value}</Typography>
      ) : (
        value
      )}
    </Box>
  );

  const renderResult = (r: AchBalanceCheckResponse) => (
    <>
      {(loading || error) && (
        <Typography className={classes.previousResult} role="status">
          {loading
            ? 'Checking again. Showing previous result.'
            : 'Latest check failed. Showing previous result.'}
        </Typography>
      )}
      <Box className={classes.rows}>
        {renderRow('Bank account', `${r.bankName || 'Bank'} •••• ${r.lastDigits || '????'}`)}
        {renderRow('Financial Connections', r.financialConnectionsAccount)}
        {renderRow(
          'Connection status',
          <Chip
            size="small"
            label={r.accountStatus}
            className={isActive ? classes.chipActive : classes.chipInactive}
          />
        )}
        {renderRow('Balance refresh', renderRefreshValue())}
        {renderRow('Available balance', formatCents(r.availableBalanceCents), true)}
        {renderRow('Current balance', formatCents(r.currentBalanceCents))}
        {renderRow('Unsettled ACH amount', formatCents(r.unsettledAchAmountCents))}
      </Box>

      <Divider className={classes.divider} />

      <Box className={classes.rows}>
        {renderRow('Balance as of', formatDateTime(r.balanceAsOf))}
        {renderRow('Last refresh attempt', formatDateTime(r.lastAttemptedAt))}
        {hasFutureRefresh &&
          renderRow('Next refresh available', formatDateTime(r.nextRefreshAvailableAt))}
      </Box>

      {renderVerdict()}

      <Typography className={classes.checkedAt}>
        Checked at {formatDateTime(checkedAt)}, your local time
      </Typography>
    </>
  );

  const note = (
    <>
      {throttled && result?.nextRefreshAvailableAt && (
        <span className={classes.noteThrottle}>
          Stripe allows another refresh in {humanizeUntil(result.nextRefreshAvailableAt, now)}.
        </span>
      )}
      Read-only risk check through Stripe Financial Connections. It does not change the user&apos;s
      balance or speed up ACH settlement.
    </>
  );

  return (
    <Dialog
      open={open}
      title="Bank balance check"
      onCancel={handleClose}
      cancelText="Close"
      onConfirm={runCheck}
      confirmText="Check again"
      confirmDisabled={throttled}
      submitting={loading}
      error={error}
      note={note}>
      {loading && !result ? (
        <Box display="flex" justifyContent="center" p={3}>
          <CircularProgress />
        </Box>
      ) : result ? (
        renderResult(result)
      ) : (
        <Typography color="textSecondary">No balance data</Typography>
      )}
    </Dialog>
  );
};

export default AchBalanceCheckDialog;
